export const CANVAS_WIDTH  = 1400;
export const CANVAS_HEIGHT = 580;
export const GRID_SIZE     = 20;

// Convert an absolute-pixel coord to normalized (0–1). Legacy rows stored
// values > 1, so anything above that threshold is treated as pixels.
export const toNorm = (val, dimension) => (val > 1 ? val / dimension : val);

export const STATUS_COLOR = {
  available: '#4CAF50',
  booked:    '#F44336',
  blocked:   '#9E9E9E',
};

// stronger colors for the read-only plans, where status matters more than style
export const STATUS_COLOR_STRONG = {
  available: '#2E7D32',
  booked:    '#C62828',
  blocked:   '#757575',
};

// Every renderer draws a table as a tabletop plus one chair per person, from
// this one layout, so a table's size always follows its number of seats:
// a table with more seats is bigger in both length and depth.
// All values are canvas units.
const TOP_DEPTH      = 50;   // short side of a 2-person tabletop
const DEPTH_PER_PAIR = 8;    // extra depth for each extra pair of seats
const SEAT_WIDTH     = 46;   // tabletop length per chair along a long side
const CHAIR_DEPTH = 9;
const CHAIR_GAP   = 2;
const CHAIR_BAND  = CHAIR_DEPTH + CHAIR_GAP;

// chairs spread evenly along one side of length `len`, centered on `pos` across it
const chairRow = (count, len, pos, vertical) => {
  const slot = len / count;
  const size = Math.min(SEAT_WIDTH * 0.7, slot * 0.75);
  return Array.from({ length: count }, (_, i) => {
    const along = slot * (i + 0.5);
    return vertical
      ? { cx: pos, cy: along, w: CHAIR_DEPTH, h: size, rotation: 0 }
      : { cx: along, cy: pos, w: size, h: CHAIR_DEPTH, rotation: 0 };
  });
};

// { w, h }: bounding box with chairs; top: tabletop; chairs: centers (cx, cy) with size and rotation
export const getTableLayout = (shape, persons) => {
  const n = Math.max(1, Math.round(Number(persons)) || 2);

  if (shape === 'circle') {
    const d = Math.max(64, Math.round((n * SEAT_WIDTH) / Math.PI));
    const size = d + CHAIR_BAND * 2;
    const r = d / 2 + CHAIR_GAP + CHAIR_DEPTH / 2;
    const chairW = Math.min(SEAT_WIDTH * 0.7, ((Math.PI * d) / n) * 0.7);
    const chairs = Array.from({ length: n }, (_, i) => {
      const a = (2 * Math.PI * i) / n - Math.PI / 2;
      return { cx: size / 2 + r * Math.cos(a), cy: size / 2 + r * Math.sin(a), w: chairW, h: CHAIR_DEPTH, rotation: (a * 180) / Math.PI + 90 };
    });
    return { w: size, h: size, top: { x: CHAIR_BAND, y: CHAIR_BAND, w: d, h: d, circle: true }, chairs };
  }

  const first = Math.ceil(n / 2);   // chairs on the first long side
  const second = n - first;         // chairs on the opposite side
  const topDepth = TOP_DEPTH + DEPTH_PER_PAIR * (first - 1);
  const len = Math.max(topDepth, first * SEAT_WIDTH);
  const depth = topDepth + CHAIR_BAND * 2;

  if (shape === 'verticalRectangle') {
    return {
      w: depth,
      h: len,
      top: { x: CHAIR_BAND, y: 0, w: topDepth, h: len },
      chairs: [
        ...chairRow(first, len, CHAIR_DEPTH / 2, true),
        ...(second ? chairRow(second, len, depth - CHAIR_DEPTH / 2, true) : []),
      ],
    };
  }
  return {
    w: len,
    h: depth,
    top: { x: 0, y: CHAIR_BAND, w: len, h: topDepth },
    chairs: [
      ...chairRow(first, len, CHAIR_DEPTH / 2, false),
      ...(second ? chairRow(second, len, depth - CHAIR_DEPTH / 2, false) : []),
    ],
  };
};

// line between two tables (normalized positions), from edge to edge, in canvas units;
// null when the tables touch. Used to show that tables are merged.
export const getLinkSegment = (a, b) => {
  const ax = a.positionX * CANVAS_WIDTH, ay = a.positionY * CANVAS_HEIGHT;
  const bx = b.positionX * CANVAS_WIDTH, by = b.positionY * CANVAS_HEIGHT;
  const dx = bx - ax, dy = by - ay;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return null;
  const ux = dx / len, uy = dy / len;
  const edge = (t, x, y) => {
    const { w, h } = getTableLayout(t.shape, t.persons);
    if (t.shape === 'circle') return w / 2;
    const tx = Math.abs(x) > 0.001 ? w / 2 / Math.abs(x) : Infinity;
    const ty = Math.abs(y) > 0.001 ? h / 2 / Math.abs(y) : Infinity;
    return Math.min(tx, ty);
  };
  const da = edge(a, ux, uy), db = edge(b, -ux, -uy);
  if (da + db >= len) return null;
  return [ax + ux * da, ay + uy * da, bx - ux * db, by - uy * db];
};

export const getDim = (shape, persons) => {
  const { w, h } = getTableLayout(shape, persons);
  return { w, h };
};
